import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/pro-solid-svg-icons';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { UploadedDocument } from '../../store/documents/documentsTypes';
import {
  DocumentStatus,
  formatAllowedRolesLabel,
  formatDocumentStatusLabel,
  isDocumentFailed,
  normalizeDocumentStatus,
} from '../../services/documents/documentStatusUtils';

interface ProcessingDocumentsListProps {
  documents: UploadedDocument[];
  isLoading?: boolean;
  onDelete?: (documentId: string, customerId: number) => void;
}

const getStatusColor = (
  status: DocumentStatus,
): 'default' | 'info' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case 'loaded':
      return 'info';
    case 'processing':
      return 'warning';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'unknown':
      return 'error';
    default:
      return 'default';
  }
};

const ProcessingDocumentsList: React.FC<ProcessingDocumentsListProps> = ({
  documents,
  isLoading = false,
  onDelete,
}) => {
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const hasFailedDocuments = documents.some((document) =>
    isDocumentFailed(document.status),
  );

  const handleDelete = async (documentId: string, customerId: number) => {
    if (!onDelete || deletingIds.has(documentId)) {
      return;
    }

    setDeletingIds((current) => new Set(current).add(documentId));
    try {
      await onDelete(documentId, customerId);
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current);
        next.delete(documentId);
        return next;
      });
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <FontAwesomeIcon icon={faFile} /> Uploaded documents
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Document history and current upload status for your account.
        {hasFailedDocuments &&
          ' Failed uploads must be deleted manually before uploading again.'}
      </Typography>

      {isLoading && documents.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : documents.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No uploaded documents yet.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2, width: '100%' }}
        >
          <Table sx={{ width: '100%' }} aria-label="uploaded documents table">
            <TableHead sx={{ backgroundColor: '#f9fafb' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  Access permissions
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => {
                const status = normalizeDocumentStatus(document.status);
                const isDeleting = deletingIds.has(document.documentId);

                return (
                  <TableRow
                    key={document.documentId}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {document.filename}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatAllowedRolesLabel(document.allowedRoles)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatDocumentStatusLabel(status)}
                        color={getStatusColor(status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {onDelete && (
                        <Tooltip title="Delete document">
                          <span>
                            <IconButton
                              onClick={() =>
                                handleDelete(
                                  document.documentId,
                                  document.customerId,
                                )
                              }
                              size="small"
                              disabled={isDeleting}
                              aria-label={`Delete ${document.filename}`}
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProcessingDocumentsList;
